import { useCallback, useEffect, useState } from 'react';

interface UseWarehouseFiltersProps {
  allLevels: number[];
  allRows: string[];
}

interface UseWarehouseFiltersResult {
  activeLevels: Set<number>;
  activeRows: Set<string>;
  toggleLevel: (level: number) => void;
  toggleRow: (rowId: string) => void;
  selectAllLevels: () => void;
  clearAllLevels: () => void;
  selectAllRows: () => void;
  clearAllRows: () => void;
}

export const useWarehouseFilters = ({
  allLevels,
  allRows,
}: UseWarehouseFiltersProps): UseWarehouseFiltersResult => {
  const [activeLevels, setActiveLevels] = useState<Set<number>>(new Set(allLevels));
  const [activeRows, setActiveRows] = useState<Set<string>>(new Set(allRows));

  // Initialize filters when allLevels or allRows change
  useEffect(() => {
    if (allLevels.length > 0) {
      setActiveLevels(new Set(allLevels));
    }
  }, [allLevels]);

  useEffect(() => {
    if (allRows.length > 0) {
      setActiveRows(new Set(allRows));
    }
  }, [allRows]);

  // Toggle level
  const toggleLevel = useCallback((level: number) => {
    setActiveLevels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(level)) {
        newSet.delete(level);
      } else {
        newSet.add(level);
      }
      return newSet;
    });
  }, []);

  // Toggle row
  const toggleRow = useCallback((rowId: string) => {
    setActiveRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  }, []);


  const selectAllLevels = useCallback(() => setActiveLevels(new Set(allLevels)), [allLevels]);
  const clearAllLevels = useCallback(() => setActiveLevels(new Set()), []);
  const selectAllRows = useCallback(() => setActiveRows(new Set(allRows)), [allRows]);
  const clearAllRows = useCallback(() => setActiveRows(new Set()), []);

  return {
    activeLevels,
    activeRows,
    toggleLevel,
    toggleRow,
    selectAllLevels,
    clearAllLevels,
    selectAllRows,
    clearAllRows,
  };
};
